import type { Node } from '@tiptap/pm/model';
import classNames from 'classnames';
import lodash from 'lodash';

import type { GIFData, ImageData, Node as RicosNode, PluginContainerData } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import {
  Node_Type,
  PluginContainerData_Alignment,
  PluginContainerData_Width_Type,
} from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import generalRTLIgnoredStyles from './../wix-rich-content-common/statics/styles/general-scss.js';
import generalStyles from './../editor-common/statics/styles/general-scss.js';
import { ORIGINAL_SHAPE_WIDTH } from './../plugin-shape/utils/consts.js';

const stylesWithRTL = { ...generalStyles, ...generalRTLIgnoredStyles };

type Nullish<T> = T | null | undefined;

const getAlignmentClassName = (alignment?: PluginContainerData_Alignment) => {
  if (!alignment || alignment === PluginContainerData_Alignment.UNRECOGNIZED) {
    return '';
  }

  return stylesWithRTL[`align${lodash.upperFirst(alignment.toLowerCase())}`];
};

const getSizeClassName = (size?: PluginContainerData_Width_Type) => {
  if (!size || size === PluginContainerData_Width_Type.UNRECOGNIZED) {
    return '';
  }

  return stylesWithRTL[`size${lodash.upperFirst(lodash.camelCase(size))}`];
};

const getTextWrapClassName = (textWrap?: boolean) =>
  textWrap === false ? stylesWithRTL.textWrapNowrap : '';

const isCustomWidthDisabled = (node: Node, isMobile: boolean) => {
  if (node.type.name === Node_Type.IMAGE) {
    const componentData = node.attrs as ImageData;

    return !!(
      isMobile &&
      componentData?.image?.width &&
      Number(componentData?.containerData?.width?.custom || 0) > 150
    );
  }

  return false;
};

const getOriginalWidth = (node: Node) => {
  switch (node.type.name) {
    case Node_Type.IMAGE:
      return (node.attrs as ImageData)?.image?.width;
    case Node_Type.GIF:
      return (node.attrs as GIFData)?.width;
    case Node_Type.SHAPE:
      return ORIGINAL_SHAPE_WIDTH;
    default:
      return undefined;
  }
};

/**
 * Returns base class names for plugin container.
 */
export const getPluginContainerClassNames = (isMobile: boolean) =>
  classNames(stylesWithRTL.pluginContainer, isMobile && stylesWithRTL.pluginContainerMobile);

/**
 * Returns class names for plugin alignment, size, and text wrapping (float).
 */
export const getPluginPlacementClassNames = (node: Node, isTextWrapEnabled: boolean) => {
  const containerData = node.attrs?.containerData as Nullish<PluginContainerData>;

  return classNames(
    getAlignmentClassName(containerData?.alignment),
    getSizeClassName(containerData?.width?.size),
    getTextWrapClassName(isTextWrapEnabled && containerData?.textWrap)
  );
};

/**
 * Returns class names for node focus and hover highlighting.
 */
export const getPluginFocusClassNames = (isFocused: boolean) =>
  classNames(
    stylesWithRTL.isFocusable,
    isFocused && stylesWithRTL.hasFocus,
    !isFocused && stylesWithRTL.hideTextSelection
  );

/**
 * Returns the width of the node based on its type, custom width, and the original width.
 */
export const getNodeWidth = (node: Node, isMobile: boolean) => {
  const containerData = node.attrs?.containerData as Nullish<PluginContainerData>;
  const disableCustomWidth = isCustomWidthDisabled(node, isMobile);

  const customWidth = !disableCustomWidth && containerData?.width?.custom;
  const originalWidth =
    (containerData?.width?.size === 'ORIGINAL' || disableCustomWidth) && getOriginalWidth(node);

  return customWidth || originalWidth;
};

/**
 * Returns the container data of a Ricos schema node.
 */
export const getContainerData = (node: RicosNode): PluginContainerData | undefined | null => {
  const data = `${lodash.camelCase(node.type)}Data`;
  return (node?.[data as $TSFixMeKey<RicosNode>] as $TSFixMe)?.containerData;
};
